import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { get } from '../../services/ApiService';
import Loader from '../../components/Loader';
import toast from 'react-hot-toast';
import moment from 'moment';
import TrackingModal from '../../components/TrackingModal';
import Pagination from '../../components/Pagination';
import { getStatus, determineCarrier } from '../../services/Helper'; 
import './tracking.css';

// Debounce hook for search
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const TrackingPage = () => {
    // State
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'bulk'
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 300);
    
    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNext: false,
        hasPrev: false
    });
    const [limit, setLimit] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    
    // Modal state for single tracking
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    
    const [bulkTrackData, setBulkTrackData] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    
    const [sortDesc, setSortDesc] = useState(true);

    // Fetch orders when filters change
    useEffect(() => {
        fetchOrders();
    }, [sortDesc, limit, currentPage, debouncedSearch]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            let query = `api/tracking/list?sort=${sortDesc ? 'desc' : 'asc'}&limit=${limit}&page=${currentPage}`;
            
            // If searching, use search query
            if (debouncedSearch.trim()) {
                query += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
            }
            
            const res = await get(query);
            if (res.success) {
                setOrders(res.orders);
                setPagination(res.pagination);
            } else {
                toast.error(res.error || 'Failed to fetch orders');
                setOrders([]);
            }
        } catch (error) {
            console.error(error);
            toast.error('Error fetching orders');
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    // Handlers
    const handleSortToggle = () => setSortDesc(!sortDesc);
    
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
        setSelectedOrders([]);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setCurrentPage(1);
        setSelectedOrders([]);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(parseInt(newLimit));
        setCurrentPage(1);
        setSelectedOrders([]);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        setSelectedOrders([]);
    };

    const handleSelectOrder = (trackingNumber) => {
        if (!trackingNumber) return;
        if (selectedOrders.includes(trackingNumber)) {
            setSelectedOrders(selectedOrders.filter(id => id !== trackingNumber));
        } else {
            if (selectedOrders.length >= 30) {
                toast.error('Maximum 30 items allowed for bulk tracking');
                return;
            }
            setSelectedOrders([...selectedOrders, trackingNumber]);
        }
    };

    const handleSelectAll = () => {
        if (selectedOrders.length === orders.length) {
            setSelectedOrders([]);
        } else {
            const allTrackingNumbers = orders.map(o => o.trackingNumber).filter(Boolean);
            setSelectedOrders(allTrackingNumbers);
        }
    };

    // Single tracking - opens modal
    const handleTrackSingle = async (order) => {
        if (!order.trackingNumber) return;
        
        setIsModalOpen(true);
        setModalLoading(true);
        setModalData({ orderInfo: order });

        const carrierType = determineCarrier(order.trackingNumber, order);

        try {
            let res;
            if (carrierType === 'AMAZON') {
                 res = await get(`api/tracking/amazon/${order.trackingNumber}`);
            } else {
                 res = await get(`api/tracking/single/${order.trackingNumber}`);
            }

            if (res.success) {
                setModalData({
                    ...res.data,
                    orderInfo: order
                });
            } else {
                setModalData({
                    orderInfo: order,
                    error: res.error || "Tracking details not found."
                });
            }
        } catch (error) {
            console.error(error);
            setModalData({
                orderInfo: order,
                error: "Failed to track item. Service may be unavailable."
            });
        } finally {
            setModalLoading(false);
        }
    };

    // Bulk tracking
    const handleTrackBulk = async () => {
        if (selectedOrders.length === 0) return;

        setViewMode('bulk');
        setBulkLoading(true);
        setBulkTrackData(null);

        try {
            const ids = selectedOrders.join(',');
            const res = await get(`api/tracking/bulk?ids=${ids}`);
            if (res.success) {
                setBulkTrackData(res.data);
            } else {
                toast.error(res.error || 'Bulk tracking failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch bulk tracking');
        } finally {
            setBulkLoading(false);
        }
    };

    // Helpers
    const getProductNames = (order) => {
        if (!order.products || order.products.length === 0) return '-';
        return order.products.map(p => p.title || p.sku || 'Unknown').join(', ');
    };

    const getCustomerName = (order) => {
        return order.shippingInfo?.fullname || order.buyerInfo?.fullname || '-';
    };

    const getStatusClass = (category) => {
        if (!category) return 'status-unknown';
        const cat = category.toLowerCase();
        if (cat === 'delivered') return 'status-delivered';
        if (cat.includes('transit') || cat.includes('progress') || cat.includes('due') || cat.includes('ready')) return 'status-transit';
        if (cat.includes('attempt') || cat.includes('issue')) return 'status-warning';
        return 'status-transit';
    };

    const isSearchMode = searchQuery.trim().length > 0;

    // Render order table
    const renderOrderTable = () => (
        <div className="order-table-container">
            <table className="order-table">
                <thead>
                    <tr>
                        <th style={{width: '50px'}}>
                            <input 
                                type="checkbox" 
                                checked={orders.length > 0 && selectedOrders.length === orders.length}
                                onChange={handleSelectAll}
                            />
                        </th>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Product</th>
                        <th>Tracking #</th>
                        <th>Order Date</th>
                        <th style={{width: '100px'}}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan="7" className="text-center p-4"><Loader /></td></tr>
                    ) : orders.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="text-center p-4 text-muted">
                                {isSearchMode ? 'No orders match your search.' : 'No orders found.'}
                            </td>
                        </tr>
                    ) : (
                        orders.map(order => (
                            <tr key={order._id} className={selectedOrders.includes(order.trackingNumber) ? 'selected-row' : ''}>
                                <td>
                                    <input 
                                        type="checkbox"
                                        checked={selectedOrders.includes(order.trackingNumber)}
                                        onChange={() => handleSelectOrder(order.trackingNumber)}
                                    />
                                </td>
                                <td className="order-id-cell">{order.orderId}</td>
                                <td>{getCustomerName(order)}</td>
                                <td className="product-cell">{getProductNames(order)}</td>
                                <td className="tracking-cell">{order.trackingNumber}</td>
                                <td>{moment(order.createdAt).format('DD MMM YYYY')}</td>
                                <td>
                                    <button 
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleTrackSingle(order)}
                                    >
                                        Track
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            
            {/* Pagination Component */}
            {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-center">
                    <Pagination 
                        currentPage={currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );

    // Render bulk tracking table
    const renderBulkTable = () => {
        if (bulkLoading) return <div className="p-5 text-center"><Loader /></div>;
        if (!bulkTrackData || !bulkTrackData.mailPieces) return <div className="text-center p-4">No tracking data available.</div>;

        const mailPieces = Array.isArray(bulkTrackData.mailPieces) ? bulkTrackData.mailPieces : [bulkTrackData.mailPieces];

        return (
            <div className="bulk-results-container">
                <div className="bulk-header">
                    <h4>Bulk Tracking Results ({mailPieces.length} items)</h4>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setViewMode('list')}>
                        ← Back to Orders
                    </button>
                </div>
                <table className="bulk-table-full">
                    <thead>
                        <tr>
                            <th>Tracking #</th>
                            <th>Product</th>
                            <th>Status</th>
                            <th>Last Event</th>
                            <th>Location</th>
                            <th>Date/Time</th>
                            <th>Summary</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mailPieces.map((mp, index) => (
                            <tr key={index}>
                                <td className="tracking-cell">{mp.mailPieceId}</td>
                                <td>{mp.summary?.productName || '-'}</td>
                                <td>
                                    <span className={`status-pill ${getStatusClass(mp.summary?.statusCategory)}`}>
                                        {mp.summary?.statusDescription || 'Unknown'}
                                    </span>
                                </td>
                                <td>{mp.summary?.lastEventName || '-'}</td>
                                <td>{mp.summary?.lastEventLocationName || '-'}</td>
                                <td>{mp.summary?.lastEventDateTime ? moment(mp.summary.lastEventDateTime).format('DD MMM, HH:mm') : '-'}</td>
                                <td className="summary-cell">{mp.summary?.summaryLine || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };



    return (
        <Layout>
            <div className="tracking-page">
                {/* Header */}
                <div className="tracking-header">
                    <div className="header-left">
                        <h2>Order Tracking</h2>
                        <span className="order-count-badge">{pagination.totalCount} orders</span>
                    </div>
                    <div className="header-right">
                        
                        {/* Limit Selector */}
                        <div className="filter-group">
                            <label><i className="fas fa-list-ol"></i> Limit</label>
                            <select 
                                className="limit-select"
                                value={limit}
                                onChange={(e) => handleLimitChange(e.target.value)}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={30}>30</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                        
                        {/* Sort Button */}
                        <button className="sort-btn" onClick={handleSortToggle} title={sortDesc ? 'Newest first' : 'Oldest first'}>
                            <i className={`fas fa-sort-${sortDesc ? 'amount-down' : 'amount-up'}`}></i>
                        </button>
                    </div>
                </div>

                {/* Toolbar / Action Bar */}
                {viewMode === 'list' && (
                    <div className="action-bar header-toolbar">
                        {/* Search Bar */}
                        <div className="search-box">
                            <i className="fas fa-search search-icon"></i>
                            <input 
                                type="text"
                                className="search-input"
                                placeholder="Search Order ID..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                            {searchQuery && (
                                <button className="search-clear" onClick={handleClearSearch}>
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>

                        {/* Bulk Track Button */}
                        <button 
                            className="btn btn-primary"
                            disabled={selectedOrders.length === 0}
                            onClick={handleTrackBulk}
                        >
                            <i className="fas fa-search"></i> Bulk Track ({selectedOrders.length})
                        </button>
                    </div>
                )}

                {/* Main Content */}
                <div className="tracking-content">
                    {viewMode === 'list' && renderOrderTable()}
                    {viewMode === 'bulk' && renderBulkTable()}
                </div>

                {/* Single Tracking Modal */}
                <TrackingModal
                    open={isModalOpen}
                    onCancel={() => setIsModalOpen(false)}
                    loading={modalLoading}
                    data={modalData}
                    orderInfo={modalData?.orderInfo}
                />
            </div>
        </Layout>
    );
};

export default TrackingPage;
